import React, { useCallback, useMemo, useState } from 'react'
import { useScreenshotStepData } from '@/tiptap/hooks/useExtensionData'
import { Editor } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { NodeViewContent, NodeViewWrapper } from '@tiptap/react'
import { getStepAction } from '@deepguide-ai/shared'
import { IconCategory, IconType } from '@deepguide-ai/types'
import { useAtom, useSetAtom } from 'jotai'
import { FeatureGate } from '@/components/plan-restrictions/FeatureGate'
import { ImageViewerDialog } from '@/components/ui/image-viewer-dialog'
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Eye,
  MousePointerClick,
  Pencil,
  Plus,
  Trash2,
  X,
  ZoomInIcon,
  ZoomOutIcon,
  SquarePen,
} from 'lucide-react'
import { nanoid } from 'nanoid'
import { editedScreenshotMapAtom } from '@/stores/imageEditorStore'
import {
  isImageEditorOpenAtom,
  saveImageEditorDataAtom,
} from '@/stores/imageEditorStore'
import { cn } from '@/lib/utils'
import { EmojiPicker } from '@/components/emoji-picker'
// @deepguide-ai/react-image-editor has been removed
type InteractionPointData = any
import {
  Button,
  IconComponent,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Dialog,
  DialogContent,
  Separator,
  Slider,
  Switch,
} from '@/components/ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from './plate-ui/dropdown-menu'
import { Toolbar, ToolbarButton } from './plate-ui/toolbar'
import { InteractionPoint } from './screenshot-extension'
import { ScreenshotRenderer } from './shared/screenshot-renderer'
import { StepAttributes } from './step-extension'

const STEP_COLORS = ['#7C9070', '#5B9BD5', '#D4845E', '#9B8FCE', '#E0917B', '#6BA5A0']

// Screenshot Toolbar Component
const ScreenshotToolbar = ({
  zoomLevel,
  onZoomChange,
  onAlignChange,
  align,
  onDelete,
  interactionPoints,
  src,
  stepId,
  reviewMode = false,
  showImageEditButton = true,
}: {
  zoomLevel: number
  onZoomChange: (zoom: number) => void
  onAlignChange: (align: 'left' | 'center' | 'right') => void
  align: 'left' | 'center' | 'right'
  onDelete: () => void
  interactionPoints: InteractionPoint[]
  src: string
  stepId: string
  /** When true, only show fullscreen button (for auditor review mode) */
  reviewMode?: boolean
  showImageEditButton?: boolean
}) => {

  const alignItems = [
    { icon: AlignLeft, value: 'left' },
    { icon: AlignCenter, value: 'center' },
    { icon: AlignRight, value: 'right' },
  ]
  const IconValue =
    alignItems.find((item) => item.value === align)?.icon ?? AlignCenter

  const [_isImageEditorOpen, setIsImageEditorOpen] = useAtom(
    isImageEditorOpenAtom
  )
  const saveImageEditorData = useSetAtom(saveImageEditorDataAtom)
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false)
  // In review mode, only show the fullscreen button
  if (reviewMode) {
    return (
      <>
        <Toolbar
          variant='media'
          className={cn(
            'absolute top-2 right-2 z-10 h-7 border-none opacity-0 transition-opacity group-hover/media:opacity-100'
          )}
          contentEditable={false}
        >
          {/* Fullscreen preview - only button in review mode */}
          <ToolbarButton
            size='none'
            variant='media'
            onClick={() => setIsFullscreenOpen(true)}
            tooltip='View fullscreen'
            className='p-0.5 hover:bg-white/10'
          >
            <Eye className='size-4' />
          </ToolbarButton>
        </Toolbar>

        <Dialog open={isFullscreenOpen} onOpenChange={setIsFullscreenOpen}>
          <DialogContent
            closeClassName='text-white'
            className='!max-w-screen w-screen border-none shadow-none h-screen p-10 rounded-none bg-black/90'
          >
            <div className='relative mx-auto '>
              <img
                src={src}
                alt='Screenshot preview'
                className='object-contain w-auto h-auto max-w-[calc(100vw-5rem)] max-h-[calc(100vh-5rem)]'
                draggable={false}
              />
              {interactionPoints?.length > 0 && interactionPoints?.map((point) => (
                <div
                  key={point.id}
                  className='absolute size-5'
                  style={{
                    left: `${point.xPercent}%`,
                    top: `${point.yPercent}%`,
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(142, 81, 255, 0.15)',
                    border: `3px solid rgb(142, 81, 255)`,
                    borderRadius: '50%',
                    boxShadow: `0 0 0 20px #8e51ff4d`,
                    zIndex: 2,
                    pointerEvents: 'none',
                  }}
                />
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </>
    )
  }

  return (
    <>
    <Toolbar
      variant='media'
      className={cn(
        'absolute top-2 right-2 z-10 h-7 border-none opacity-0 transition-opacity group-hover/media:opacity-100'
      )}
      contentEditable={false}
    >
      {/* Zoom Controls */}
      <ToolbarButton
        size='none'
        variant='media'
        onClick={() => onZoomChange(Math.max(1, zoomLevel - 0.25))}
        tooltip='Zoom out'
        disabled={zoomLevel <= 1}
        className={cn(
          'p-0.5 hover:bg-white/10',
          zoomLevel <= 1 && 'opacity-50'
        )}
      >
        <ZoomOutIcon className='size-4' />
      </ToolbarButton>
      <div className='flex items-center px-2'>
        <Slider
          value={[zoomLevel]}
          max={10}
          min={1}
          step={0.25}
          onValueChange={(value) => onZoomChange(value[0])}
          className='w-12 [&_[data-slot=slider-range]]:bg-white [&_[data-slot=slider-thumb]]:h-3 [&_[data-slot=slider-thumb]]:w-3 [&_[data-slot=slider-thumb]]:border-0 [&_[data-slot=slider-thumb]]:bg-white [&_[data-slot=slider-thumb]]:shadow-sm [&_[data-slot=slider-track]]:h-1 [&_[data-slot=slider-track]]:bg-white/30'
        />
      </div>
      <div className='flex w-8 items-center justify-center px-1 text-xs text-white'>
        {zoomLevel.toFixed(1)}x
      </div>
      <ToolbarButton
        size='none'
        variant='media'
        onClick={() => onZoomChange(Math.min(10, zoomLevel + 0.25))}
        tooltip='Zoom in'
        disabled={zoomLevel >= 10}
        className={cn(
          'p-0.5 hover:bg-white/10',
          zoomLevel >= 10 && 'opacity-50'
        )}
      >
        <ZoomInIcon className='size-4' />
      </ToolbarButton>

      {/* Fullscreen preview */}
      <ToolbarButton
        size='none'
        variant='media'
        onClick={() => setIsFullscreenOpen(true)}
        tooltip='View fullscreen'
        className='p-0.5 hover:bg-white/10'
      >
        <Eye className='size-4' />
      </ToolbarButton>

      {/* Alignment Dropdown */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <ToolbarButton
            size='none'
            variant='media'
            className='p-0.5 hover:bg-white/10'
            tooltip='Align'
          >
            <IconValue className='size-4' />
          </ToolbarButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className='min-w-0 rounded-md border-none bg-black/60 p-0 shadow-none'
          align='end'
        >
          <DropdownMenuRadioGroup
            className='flex hover:bg-transparent'
            value={align}
            onValueChange={(value) =>
              onAlignChange(value as 'left' | 'center' | 'right')
            }
          >
            {alignItems.map(({ icon: Icon, value: itemValue }) => (
              <DropdownMenuRadioItem
                key={itemValue}
                className='size-[26px] text-white opacity-60 hover:opacity-100 data-[state=checked]:bg-black/5 data-[state=checked]:opacity-100'
                value={itemValue}
                hideIcon
              >
                <Icon className='size-4 h-4 w-4 text-white' />
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {/* edit screenshot */}
      {showImageEditButton && (
        <FeatureGate flag='imageEditor'>
          <ToolbarButton
            size='none'
            variant='media'
            onClick={() => {
              // 转换InteractionPoint为InteractionPointData类型
              const initialInteractionPoints: InteractionPointData[] =
                interactionPoints?.map((point) => ({
                  id: point.id,
                  tool: 'interactionPoint',
                  x: point.xPercent,
                  y: point.yPercent,
                  type:
                    point.type === 'Click' ||
                    point.type === 'Hover' ||
                    point.type === 'Input' ||
                    point.type === 'Submit' ||
                    point.type === 'Scroll'
                      ? point.type
                      : 'Click',
                })) || []

              // 保存图像编辑器数据
              saveImageEditorData({
                src,
                initialInteractionPoints,
                stepId,
                // initialAnnotations 将在 DocEditorLayout 中根据 stepId 懒加载
              })

              setIsImageEditorOpen(true)
            }}
            tooltip='Edit screenshot'
            className='p-0.5 hover:bg-white/10'
          >
            <SquarePen className='size-4' />
          </ToolbarButton>
        </FeatureGate>
      )}
      {/* Delete Button */}
      <ToolbarButton
        size='none'
        variant='media'
        onClick={onDelete}
        tooltip='Remove screenshot '
        className='p-0.5 hover:bg-red-500/20 hover:text-red-400'
      >
        <Trash2 className='size-4' />
      </ToolbarButton>
    </Toolbar>
    <ImageViewerDialog
        open={Boolean(isFullscreenOpen)}
        imageUrl={src}
        onOpenChange={(open) => {
          if (!open) {
            setIsFullscreenOpen(false);
          }
        }}
      />
    {/* <Dialog open={isFullscreenOpen} onOpenChange={setIsFullscreenOpen}>
      <DialogContent
        closeClassName='text-white'
        className='!max-w-screen w-screen border-none shadow-none h-screen p-10 rounded-none bg-black/90'
      >
        <div className='relative mx-auto '>
            <img
            src={src}
            alt='Screenshot preview'
            className='object-contain w-auto h-auto max-w-[calc(100vw-5rem)] max-h-[calc(100vh-5rem)]'
            draggable={false}
          />
          {interactionPoints?.length > 0 && interactionPoints?.map((point) => (
            <div
              key={point.id}
              className='absolute size-5'
              style={{
                left: `${point.xPercent}%`,
                top: `${point.yPercent}%`,
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(142, 81, 255, 0.15)',
                border: `3px solid rgb(142, 81, 255)`,
                borderRadius: '50%',
                boxShadow: `0 0 0 20px #8e51ff4d`,
                zIndex: 2,
                pointerEvents: 'none',
              }}
            />
            ))
          }
        </div>
      </DialogContent>
    </Dialog> */}
    </>
  )
}

interface StepNodeViewProps {
  node: ProseMirrorNode
  editor: Editor
  getPos: () => number | undefined
  updateAttributes: (attributes: Record<string, any>) => void
  deleteNode: () => void
  selected: boolean
}

export function StepNodeView({
  node,
  editor,
  getPos,
  updateAttributes,
  deleteNode,
  selected,
}: StepNodeViewProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [isEditingTitle, setIsEditingTitle] = useState(false)

  const attrs = node.attrs as StepAttributes
  const stepTitle = attrs.title || ''
  const guideAction = attrs.guideAction || ''
  const showTitle = attrs.showTitle // Default to false
  const showCaption = attrs.showCaption || false // Default to false
  const icon = attrs.icon || 'sparkles'
  const iconType = (attrs.iconType || 'REGULAR') as IconType
  const iconCategory = (attrs.iconCategory || 'LUCIDE') as IconCategory
  const iconColor = attrs.iconColor || '#3b82f6'
  const showIcon = attrs.showIcon || false
  const cropStyle = attrs.cropStyle || 'cropped'

  const [editingTitle, setEditingTitle] = useState(stepTitle)
  // Temporary state for popover editing
  const [tempTitle, setTempTitle] = useState(stepTitle)
  const [tempGuideAction, setTempGuideAction] = useState(guideAction)
  const [tempShowTitle, setTempShowTitle] = useState(showTitle)
  const [tempIcon, setTempIcon] = useState(icon)
  const [tempIconType, setTempIconType] = useState(iconType)
  const [tempIconCategory, setTempIconCategory] = useState(iconCategory)
  const [tempIconColor, setTempIconColor] = useState(iconColor)
  const [tempShowIcon, setTempShowIcon] = useState(showIcon)
  const [tempCropStyle, setTempCropStyle] = useState(cropStyle)
  const [_tempScreenshotZoomLevel, setTempScreenshotZoomLevel] = useState(
    attrs.screenshotZoomLevel || 1
  )

  // Update editing title when attrs.title changes
  React.useEffect(() => {
    if (!isEditingTitle) {
      setEditingTitle(stepTitle)
    }
  }, [stepTitle, isEditingTitle])

  // Calculate step number based on position within parent Steps container.
  // Pure — must not write to the document here. `updateAttributes` inside a
  // useMemo synchronously dispatches a ProseMirror transaction while `Step`
  // is still rendering, which React flags ("Cannot update a component while
  // rendering a different component") and which cascades into a runaway
  // re-render loop across every Step in the doc — pegs the tab's CPU and
  // stalls Puppeteer PDF capture on step-heavy documents. The correction is
  // applied in the effect below instead, after render.
  const stepNumber = useMemo(() => {
    try {
      const pos = getPos()
      if (pos === undefined) return attrs.number || 1

      const $pos = editor.state.doc.resolve(pos)
      let stepsNode: ProseMirrorNode | null = null
      let stepsPos = 0

      // Walk up the tree to find the Steps parent
      for (let depth = $pos.depth; depth > 0; depth--) {
        const node = $pos.node(depth)
        if (node.type.name === 'Steps') {
          stepsNode = node
          stepsPos = $pos.before(depth)
          break
        }
      }

      if (!stepsNode) return attrs.number || 1

      // Count Step nodes within the Steps container
      let stepCount = 0
      stepsNode.forEach((child, offset) => {
        const childPos = stepsPos + offset + 1
        if (child.type.name === 'Step' && childPos < pos) {
          stepCount++
        }
      })

      return stepCount + 1
    } catch (error) {
      console.error('Error calculating step number', error)
      return attrs.number || 1
    }
  }, [editor.state.doc, getPos, attrs.number])

  React.useEffect(() => {
    if (stepNumber !== attrs.number) {
      updateAttributes({ number: stepNumber })
    }
  }, [stepNumber, attrs.number, updateAttributes])

  // Extract caption text from first paragraph in node content
  // NOTE: Must be after stepNumber calculation since it depends on stepNumber
  const captionText = useMemo(() => {
    if (!showCaption) {
      return '';
    }

    if (attrs.title) {
      return attrs.title;
    }

    if (!node.content || node.content.size === 0) {
      return '';
    }

    // Find first paragraph node
    let firstParagraphText = '';
    node.content.forEach((child) => {
      if (child.type.name === 'paragraph' && !firstParagraphText) {
        // Extract text content from paragraph
        child.content?.forEach((textNode) => {
          if (textNode.type.name === 'text' && textNode.text) {
            firstParagraphText += textNode.text;
          }
        });
      }
    });

    // Limit to 150 characters
    const caption = firstParagraphText.substring(0, 150);
    return caption;
  }, [node.content, showCaption, attrs.title]);

  // Function to save settings from popover
  const saveSettings = useCallback(() => {
    const newAttributes: Partial<StepAttributes> = {}

    if (tempTitle.trim() !== stepTitle)
      newAttributes.title = tempTitle.trim() || ''
    if (tempGuideAction.trim() !== guideAction)
      newAttributes.guideAction = tempGuideAction.trim() || ''
    if (tempShowTitle !== showTitle) newAttributes.showTitle = tempShowTitle
    if (tempIcon !== icon) newAttributes.icon = tempIcon
    if (tempIconType !== iconType) newAttributes.iconType = tempIconType
    if (tempIconCategory !== iconCategory)
      newAttributes.iconCategory = tempIconCategory
    if (tempIconColor !== iconColor) newAttributes.iconColor = tempIconColor

    if (tempShowIcon !== showIcon) {
      newAttributes.showIcon = tempShowIcon
      if (tempShowIcon) {
        if (!newAttributes.icon) newAttributes.icon = 'sparkles'
        if (!newAttributes.iconType) newAttributes.iconType = 'REGULAR'
        if (!newAttributes.iconCategory) newAttributes.iconCategory = 'LUCIDE'
        if (!newAttributes.iconColor) newAttributes.iconColor = '#3b82f6'
      }
    }

    if (tempCropStyle !== cropStyle) newAttributes.cropStyle = tempCropStyle

    if (Object.keys(newAttributes).length > 0) {
      updateAttributes(newAttributes)
    }
    setIsPopoverOpen(false)
  }, [
    tempTitle,
    stepTitle,
    tempGuideAction,
    guideAction,
    tempShowTitle,
    showTitle,
    tempIcon,
    icon,
    tempIconType,
    iconType,
    tempIconCategory,
    iconCategory,
    tempIconColor,
    iconColor,
    tempShowIcon,
    showIcon,
    tempCropStyle,
    cropStyle,
    updateAttributes,
  ])

  // Function to cancel settings changes
  const cancelSettings = useCallback(() => {
    setTempTitle(stepTitle)
    setTempGuideAction(guideAction)
    setTempShowTitle(showTitle)
    setTempIcon(icon)
    setTempIconType(iconType)
    setTempIconCategory(iconCategory)
    setTempIconColor(iconColor)
    setTempShowIcon(showIcon)
    setTempCropStyle(cropStyle)
    setTempScreenshotZoomLevel(attrs.screenshotZoomLevel || 1)
    setIsPopoverOpen(false)
  }, [
    stepTitle,
    guideAction,
    showTitle,
    icon,
    iconType,
    iconCategory,
    iconColor,
    showIcon,
    cropStyle,
    attrs.screenshotZoomLevel,
  ])

  // Function to open settings popover
  const openSettings = useCallback(() => {
    setTempTitle(stepTitle)
    setTempGuideAction(guideAction)
    setTempShowTitle(showTitle)
    setTempIcon(icon)
    setTempIconType(iconType)
    setTempIconCategory(iconCategory)
    setTempIconColor(iconColor)
    setTempShowIcon(showIcon)
    setTempCropStyle(cropStyle)
    setTempScreenshotZoomLevel(attrs.screenshotZoomLevel || 1)
    setIsPopoverOpen(true)
  }, [
    stepTitle,
    guideAction,
    showTitle,
    icon,
    iconType,
    iconCategory,
    iconColor,
    showIcon,
    cropStyle,
    attrs.screenshotZoomLevel,
  ])

  // Function to handle adding a new step after this one
  const handleAddStep = useCallback(() => {
    const pos = getPos()
    if (pos === undefined) return

    editor.commands.insertContentAt(pos + node.nodeSize, {
      type: 'Step',
      attrs: {
        id: nanoid(10),
        title: `Step ${stepNumber + 1}`,
        showTitle: true,
        showIcon: false,
      },
      content: [
        {
          type: 'paragraph',
          content: [],
        },
      ],
    })
  }, [editor, getPos, node.nodeSize, stepNumber])

  // Function to handle deleting this step
  const handleDeleteStep = useCallback(() => {
    deleteNode()
  }, [deleteNode])

  // Function to start editing the title
  const handleTitleClick = useCallback(() => {
    if (editor.isEditable) {
      setIsEditingTitle(true)
      setEditingTitle(stepTitle)
    }
  }, [editor.isEditable, stepTitle])

  // Function to save the edited title
  const handleTitleSave = useCallback(() => {
    if (editingTitle.trim() !== stepTitle) {
      updateAttributes({ title: editingTitle.trim() || 'Step' })
    }
    setIsEditingTitle(false)
  }, [editingTitle, stepTitle, updateAttributes])

  // Function to cancel editing
  const handleTitleCancel = useCallback(() => {
    setEditingTitle(stepTitle)
    setIsEditingTitle(false)
  }, [stepTitle])

  // Function to handle key press in title input
  const handleTitleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleTitleSave()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        handleTitleCancel()
      }
    },
    [handleTitleSave, handleTitleCancel]
  )

  const stepId = attrs.id || nanoid(10)

  // 获取全局已编辑截图映射
  const [editedMap] = useAtom(editedScreenshotMapAtom)
  // Fetch screenshot data only for likely DB IDs (avoid fetching for human-friendly IDs like "step-save-todo")
  // dE3G5MkCp6r_MG_IJ_fvu
  const isLikelyDbId = !!stepId && (stepId.length === 21 || stepId.startsWith('step-'))  // && /^[a-z0-9\\-_]{21,}$/.test(stepId);
  const shouldFetchStep = !!stepId && isLikelyDbId && cropStyle !== 'none'
  const { step: stepData, isLoading: isLoadingStep } = useScreenshotStepData({
    stepId: shouldFetchStep ? stepId : undefined,
    enabled: shouldFetchStep,
  })

  // 优先使用保存后的 editedUrl（包括后端返回的 editedUrl），其次节点属性上的 screenshotUrl，最后后端原始 url
  const screenshotUrl =
    editedMap[stepId]?.url ||
    stepData?.screenshot?.editedUrl ||
    attrs.screenshotUrl ||
    stepData?.screenshot?.url
  // Use viewportInfo dimensions for consistent aspect ratio with interaction points
  // This ensures the container matches the original capture viewport
  const screenshotWidth =
    attrs.screenshotWidth || stepData?.viewportInfo?.width || stepData?.screenshot?.width || 800
  const screenshotHeight =
    attrs.screenshotHeight || stepData?.viewportInfo?.height || stepData?.screenshot?.height || 600
  // 分享/只读页无 stepByIdApiResolver，stepData 不可得，回退到 attrs 中的持久化数据
  const elementBox =
    editedMap[stepId]?.viewportCoordinates ||
    stepData?.viewportCoordinates ||
    attrs.elementBox

  const interactionPoints: InteractionPoint[] = useMemo(() => {
    const viewportCoordinates =
      editedMap[stepId]?.viewportCoordinates || stepData?.viewportCoordinates

    if (stepData?.viewportInfo && viewportCoordinates) {
      // viewportInfo 尺寸是原始捕获尺寸，与截图容器基准一致
      const refWidth = stepData.viewportInfo.width
      const refHeight = stepData.viewportInfo.height

      return [
        {
          id: stepData.id,
          type: stepData.action || 'Click',
          x: viewportCoordinates.center.x,
          y: viewportCoordinates.center.y,
          width: viewportCoordinates.width,
          height: viewportCoordinates.height,
          xPercent: (viewportCoordinates.center.x / refWidth) * 100,
          yPercent: (viewportCoordinates.center.y / refHeight) * 100,
          widthPercent: (viewportCoordinates.width / refWidth) * 100,
          heightPercent: (viewportCoordinates.height / refHeight) * 100,
          label: getStepAction(stepData) ?? '',
          style: stepData.action,
        },
      ]
    }

    if (Array.isArray(attrs.interactionPoints) && attrs.interactionPoints.length > 0) {
      return attrs.interactionPoints as InteractionPoint[]
    }

    return []
  }, [stepData, editedMap, stepId, attrs.interactionPoints])

  // Update node attributes when screenshot data is loaded from database
  React.useEffect(() => {
    if (!stepData?.screenshot?.url || isLoadingStep || !editor.isEditable)
      return

    // Use setTimeout to ensure the update happens after the editor is ready
    const timeoutId = setTimeout(() => {
      const pos = getPos()
      if (pos === undefined) return

      const updates: Partial<StepAttributes> = {}
      let hasUpdates = false

      // Update screenshot URL if not already set (prefer editedUrl)
      const preferredUrl =
        stepData.screenshot.editedUrl || stepData.screenshot.url
      if (!attrs.screenshotUrl && preferredUrl) {
        updates.screenshotUrl = preferredUrl
        hasUpdates = true
      }

      // Update width if not already set
      if (stepData.screenshot.width && !attrs.screenshotWidth) {
        updates.screenshotWidth = stepData.screenshot.width
        hasUpdates = true
      }

      // Update height if not already set
      if (stepData.screenshot.height && !attrs.screenshotHeight) {
        updates.screenshotHeight = stepData.screenshot.height
        hasUpdates = true
      }

      // Update interaction points if not already set
      if (interactionPoints.length > 0 && !attrs.interactionPoints) {
        updates.interactionPoints = interactionPoints
        hasUpdates = true
      }

      // Update element box if not already set
      if (elementBox && !attrs.elementBox) {
        updates.elementBox = elementBox
        hasUpdates = true
      }

      // console.log('hasUpdates', updates, attrs)
      if (hasUpdates) {
        updateAttributes(updates)
      }
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [
    stepData,
    isLoadingStep,
    attrs.screenshotUrl,
    attrs.screenshotWidth,
    attrs.screenshotHeight,
    attrs.interactionPoints,
    attrs.elementBox,
    interactionPoints,
    elementBox,
    updateAttributes,
    editor.isEditable,
    getPos,
  ])

  const stepColor = STEP_COLORS[(stepNumber - 1) % STEP_COLORS.length]

  const stepNumberColor = 'rgb(124, 144, 112)'

  return (
    <NodeViewWrapper
      className={cn(
        'group/step relative mb-8',
        selected && 'ProseMirror-selectednode'
      )}
      data-type='Step'
      data-dg-id={stepId}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Step content */}
      <div className='step-content relative flex flex-col gap-4'>
        {/* Step header with number and title */}
        <div className='flex items-center gap-3'>
          {/* Step number badge */}
          <div
            className='flex h-8 w-8 shrink-0 items-center justify-center rounded-md font-[Fraunces] text-base font-medium text-white'
            style={{ backgroundColor: stepNumberColor }}
          >
            {/* Action buttons - appears on hover (hidden in review mode) */}
            {editor.isEditable && !(editor as any).reviewMode && (isHovered || isPopoverOpen) ? (
              <div
                contentEditable={false}
                className={`pointer-events-auto z-10 flex h-full gap-0 transition-opacity ${isHovered || isPopoverOpen ? 'opacity-100' : 'opacity-0'
                  }`}
              >
                {/* Settings Popover */}
                <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant='ghost'
                      size='menuAction'
                      className='h-8 w-8 rounded-md text-white hover:bg-white/25'
                      onClick={openSettings}
                    >
                      <Pencil className='h-4 w-4' />
                    </Button>
                  </PopoverTrigger>

                  <PopoverContent
                    className='w-[340px] rounded-2xl border border-sidebar-border bg-white p-0 shadow-[0_8px_40px_0_rgba(0,0,0,0.08)]'
                    align='start'
                    side='bottom'
                    sideOffset={8}
                  >
                    <div className='flex flex-col'>
                      {/* Header */}
                      <div className='flex items-center justify-between border-b border-sidebar-border px-6 py-5'>
                        <h3 className='font-[Fraunces] text-[20px] leading-[1.2] font-medium text-sidebar-foreground'>
                          Step Settings
                        </h3>
                        <Button
                          variant='ghost'
                          size='menuAction'
                          onClick={cancelSettings}
                          className='h-8 w-8 text-[#86888F] hover:bg-muted hover:text-[#43464D]'
                        >
                          <X className='h-4 w-4' />
                        </Button>
                      </div>

                      <div className='space-y-4 px-6 py-6'>
                        {/* Show Title Toggle */}
                        <div className='flex items-center justify-between'>
                          <label className='text-[13px] leading-[1.2] font-normal text-[#86888F]'>
                            Show Title
                          </label>
                          <Switch
                            checked={tempShowTitle}
                            onCheckedChange={() => setTempShowTitle(!tempShowTitle)}
                          />
                        </div>

                        {/* Title Input */}
                        <div className='flex flex-col gap-2'>
                          <label className='text-[13px] leading-[1.2] font-normal text-[#1F2024]'>
                            Title
                          </label>
                          <div className='flex h-[42px] items-center gap-0'>
                            <div className='flex  w-11 shrink-0 items-center justify-between   bg-white'>
                              <EmojiPicker
                                selectedIcon={tempShowIcon ? tempIcon : undefined}
                                iconColor={tempIconColor}
                                iconCategory={tempIconCategory}
                                onEmojiSelect={(emoji) => {
                                  setTempShowIcon(true)
                                  setTempIcon(emoji)
                                  setTempIconCategory('EMOJI')
                                }}
                                onIconChange={(
                                  iconName: string,
                                  iconCategory: IconCategory
                                ) => {
                                  if (iconName === '') {
                                    setTempIconCategory('LUCIDE')
                                    setTempShowIcon(false)
                                    return
                                  }
                                  setTempShowIcon(true)
                                  setTempIcon(iconName)
                                  setTempIconCategory(iconCategory)
                                }}
                                onColorChange={setTempIconColor}
                                isPopoverMode={true}
                              />
                            </div>
                            <input
                              type='text'
                              value={tempTitle}
                              onChange={(e) => setTempTitle(e.target.value)}
                              placeholder='Enter step title...'
                              className='placeholder:text-[#85878D] focus-visible:ring-ring h-[36px] w-full rounded-md border border-[#E3E1DD]  px-3 text-[13px] leading-[1.2] font-normal text-[#43464D] transition-colors focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50'
                            />
                          </div>
                        </div>
                      </div>

                      {/* <div className='space-y-5'>
                        <div className='flex items-center justify-between'>
                          <label className='text-muted-foreground text-xs leading-4'>
                            Show Icon
                          </label>
                          <Switch
                            checked={tempShowIcon}
                            onCheckedChange={() => setTempShowIcon(!tempShowIcon)}
                          />
                        </div>

      s                  {tempShowIcon && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <IconEditor
                              selectedIcon={tempIcon}
                              iconType={tempIconType}
                              iconCategory={tempIconCategory}
                              iconColor={tempIconColor}
                              onIconChange={setTempIcon}
                              onTypeChange={setTempIconType}
                              onCategoryChange={setTempIconCategory}
                              onColorChange={setTempIconColor}
                            />
                          </div>
                        )}
                      </div> */}
                      {/* <div className='flex flex-col gap-2'>
                        <label className='text-muted-foreground text-xs leading-4'>
                          Icon
                        </label>
                        <EmojiPicker
                          selectedIcon={tempShowIcon ? tempIcon : undefined}
                          iconColor={tempIconColor}
                          iconCategory={tempIconCategory}
                          onEmojiSelect={(emoji) => {
                            setTempShowIcon(true)
                            setTempIcon(emoji)
                            setTempIconCategory('EMOJI')
                          }}
                          onIconChange={(
                            iconName: string,
                            iconCategory: IconCategory
                          ) => {
                            if (iconName === '') {
                              setTempIconCategory('LUCIDE')
                              setTempShowIcon(false)
                              return
                            }
                            setTempShowIcon(true)
                            setTempIcon(iconName)
                            setTempIconCategory(iconCategory)
                          }}
                          onColorChange={setTempIconColor}
                          isPopoverMode={true}
                        />
                      </div> */}

                      {/* Screenshot Crop Style */}
                      {/* 暂时先注释 */}
                      {/* <div className='flex flex-col gap-2'>
                        <label className='text-muted-foreground text-xs leading-4'>
                          Screenshot Display
                        </label>
                        <Select
                          value={tempCropStyle}
                          onValueChange={(value: 'cropped' | 'full' | 'none') =>
                            setTempCropStyle(value)
                          }
                        >
                          <SelectTrigger className='h-9 w-full'>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='cropped'>Cropped (Default)</SelectItem>
                            <SelectItem value='full'>Full Screenshot</SelectItem>
                            <SelectItem value='none'>No Screenshot</SelectItem>
                          </SelectContent>
                        </Select>
                      </div> */}

                      <Separator className='m-0 bg-sidebar-border' />

                      {/* Action Buttons */}
                      <div className='flex items-center justify-between  px-6 pt-[14px] pb-4'>
                        <Button
                          variant='destructive'
                          className='h-9 bg-transparent px-0 text-[13px] leading-[1.1] font-normal text-[#C24646] shadow-none hover:bg-transparent hover:text-[#C24646]'
                          size='sm'
                          onClick={handleDeleteStep}
                        >
                          Delete
                        </Button>
                        <div className='flex items-center space-x-2'>
                          <Button
                            variant='outline'
                            size='sm'
                            className='h-9 w-24 rounded-lg border-[#E3E1DD] px-0 text-[13px] text-[#43464D] hover:bg-muted'
                            onClick={cancelSettings}
                          >
                            Cancel
                          </Button>
                          <Button
                            size='sm'
                            className='h-9 w-[120px] rounded-lg bg-sidebar-primary px-0 text-[13px] text-white hover:bg-[#6f8264]'
                            onClick={saveSettings}
                          >
                            Save Changes
                          </Button>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            ) : stepNumber
            }
          </div>

          {/* Title and icon */}
          <div className='flex flex-1 items-center gap-2'>
              {showIcon && (
                <IconComponent
                  iconName={icon}
                  iconType={iconType}
                  iconCategory={iconCategory}
                  iconColor={iconColor}
                  size={20}
                  className={`transition-colors ${
                    iconCategory === 'EMOJI' ? 'h-[20px] text-[20px]' : ''
                  }`}
                />
              )}
              {(showTitle || stepTitle) && (
                <>
                  {isEditingTitle ? (
                    <input
                      type='text'
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onBlur={handleTitleSave}
                      onKeyDown={handleTitleKeyDown}
                      autoFocus
                      className='w-full border-none bg-transparent font-[Fraunces] text-lg font-medium text-sidebar-foreground outline-none'
                      placeholder='Enter step title...'
                    />
                  ) : (
                    <h3
                      className='hover:bg-accent -mx-1 !mb-0 !mt-0 cursor-pointer rounded px-1 font-[Fraunces] text-lg font-medium text-sidebar-foreground transition-colors'
                      onClick={handleTitleClick}
                      contentEditable={false}
                   
                    >
                      {stepTitle || 'Click to add title'}
                    </h3>
                  )}
                </>
              )}
          </div>
        </div>

            {/* Step body content */}
            <div className="step-content-body text-[15px] text-muted-foreground leading-[1.5] font-['Plus_Jakarta_Sans']">
              <NodeViewContent />
            </div>

            {/* Screenshot shimmer placeholder while loading */}
            {!screenshotUrl && isLoadingStep && cropStyle !== 'none' && (
              <div className='relative overflow-hidden rounded-xl border border-sidebar-border bg-sidebar-border' style={{ aspectRatio: '16 / 10' }}>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-[shimmer_1.5s_ease-in-out_infinite]" style={{ backgroundSize: '200% 100%' }} />
              </div>
            )}

            {/* Screenshot */}
            {screenshotUrl && (
              <div className='group/media relative w-full overflow-hidden rounded-xl border border-sidebar-border'>
                <ScreenshotRenderer
                  url={screenshotUrl}
                  width={screenshotWidth}
                  height={screenshotHeight}
                  elementBox={elementBox}
                  interactionPoints={interactionPoints}
                  cropStyle={cropStyle}
                  align={attrs.align || 'center'}
                  alt={`Screenshot for ${stepTitle || 'step'}`}
                  zoomLevel={attrs.screenshotZoomLevel || 1}
                  panX={attrs.screenshotPanX ?? 0}
                  panY={attrs.screenshotPanY ?? 0}
                  onZoomChange={(zoom) => {
                    updateAttributes({
                      screenshotZoomLevel: zoom,
                      // Reset pan when zoom changes to 1 or below
                      ...(zoom <= 1
                        ? { screenshotPanX: 0, screenshotPanY: 0 }
                        : {}),
                    })
                  }}
                  onAlignChange={(align) => updateAttributes({ align })}
                  onWidthChange={(newWidth) => {
                    const newHeight =
                      (newWidth / screenshotWidth) * screenshotHeight
                    updateAttributes({
                      screenshotWidth: newWidth,
                      screenshotHeight: newHeight,
                    })
                  }}
                  onPanChange={(panX, panY) => {
                    updateAttributes({
                      screenshotPanX: panX,
                      screenshotPanY: panY,
                    })
                  }}
                  readOnly={!editor.isEditable}
                  isFullScreenshot={true}
                />
                {/* Screenshot Toolbar - shows only fullscreen button in review mode */}
                {(editor.isEditable || (editor as any).reviewMode) && (
                  <ScreenshotToolbar
                    zoomLevel={attrs.screenshotZoomLevel || 1}
                    onZoomChange={(zoom) => {
                      updateAttributes({
                        screenshotZoomLevel: zoom,
                        // Reset pan when zoom changes to 1 or below
                        ...(zoom <= 1
                          ? { screenshotPanX: 0, screenshotPanY: 0 }
                          : {}),
                      })
                    }}
                    onAlignChange={(align) => updateAttributes({ align })}
                    align={attrs.align || 'center'}
                    onDelete={() => {
                      // Remove the screenshot attributes but keep the step
                      updateAttributes({
                        screenshotUrl: null,
                        screenshotWidth: null,
                        screenshotHeight: null,
                        cropStyle: 'none',
                      })
                    }}
                    interactionPoints={interactionPoints}
                    src={screenshotUrl}
                    stepId={attrs.id || stepId}
                    reviewMode={(editor as any).reviewMode || false}
                    showImageEditButton={!editor.isEditable}
                  />
                )}
              </div>
            )}

            {/* Caption below screenshot (PDF-only) */}
            {screenshotUrl && showCaption && captionText && (
              <div
                className='text-center text-sm italic text-gray-600'
                contentEditable={false}
              >
                <span className='font-semibold text-gray-900'>Step {stepNumber} —</span> {captionText}
              </div>
            )}

            {/* Guide action callout */}
            {guideAction && (
              <div
                className="flex items-center gap-2 rounded-md px-4 py-3"
                style={{
                  backgroundColor: `${stepColor}10`,
                  borderLeft: `3px solid ${stepColor}`,
                }}
                contentEditable={false}
              >
                <MousePointerClick className="h-4 w-4 shrink-0" style={{ color: stepColor }} />
                <span className="font-['Plus_Jakarta_Sans'] text-sm font-semibold text-sidebar-foreground">
                  {guideAction}
                </span>
              </div>
            )}

      </div>
    </NodeViewWrapper>
  )
}
